import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import React from "react";
import { CART_QUERY } from "components/Cart/queries";
import Container from "components/common/Container";
import Link from "components/common/Link";
import ProfileForm from "components/common/ProfileForm";
import { MY_COURSES_URL, PRIVACY_URL, PROFILE_URL } from "constants/urls";
import { getUsernameFromEmail } from "helpers/strings";
import { goToSafeUrl } from "helpers/urls";
import { useReCaptchaVerify } from "hooks/useReCaptchaVerify";
import { useRouterQuery } from "hooks/useRouterQuery";
import {
  RegisterUserMutationMutation as RegisterUserMutation,
  RegisterUserMutationVariables,
} from "./__generated__/Register";
import Page from "./common/Page";

const REGISTER_MUTATION = gql`
  mutation RegisterUserMutation($input: RegisterUserInput!) {
    registerUser(input: $input) {
      user {
        id
        username
        email
        userFields {
          isNewlyMigrated
          addressLine1
          addressLine2
          city
          country
          degree
          faxNumber
          licenseData {
            licenseNumber
            state
            type
          }
          nameMiddleInitial
          namePrefix
          newsletterOptOut
          organization
          phoneNumber
          state
          zip
        }
      }
    }
  }
`;

export default function Register() {
  const {
    routerQuery: { nextPage = MY_COURSES_URL },
  } = useRouterQuery<{ nextPage?: string }>();
  const verifyReCaptcha = useReCaptchaVerify();
  const [register] = useEnhancedMutation<RegisterUserMutation, RegisterUserMutationVariables>(
    REGISTER_MUTATION,
    {},
    { auth: false }
  );

  return (
    <div className="max-w-3xl mx-auto">
      <Container>
        <Page title="Sign Up">
          <ProfileForm
            onSubmit={async (values, { setFormSuccess }) => {
              const isVerified = await verifyReCaptcha("register");
              if (!isVerified) {
                throw new Error(
                  "Failed to register because you failed a bot check; please contact Support."
                );
              }

              const { data } = await register({
                variables: {
                  input: {
                    ...values,
                    username: getUsernameFromEmail(values.email),
                    licenseData: JSON.stringify(values.licenseData),
                  },
                },
                refetchQueries: [{ query: CART_QUERY }],
              });
              if (data?.registerUser?.user) {
                setFormSuccess("Signed up! Logging you in, please wait...");
                window.scrollTo({ top: 0, behavior: "smooth" });
                goToSafeUrl(
                  data.registerUser.user?.userFields?.isNewlyMigrated ? PROFILE_URL : nextPage,
                  MY_COURSES_URL
                );
              } else {
                throw new Error("Failed to register");
              }
            }}
          />
          <p className="mt-12 text-xs text-gray-500">
            By submitting this form, you agree to allow your data to be processed for the purpose of
            your participation in activities on this section of this site. Please review the{" "}
            <Link href={PRIVACY_URL}>Privacy Policy</Link> that covers how products and services
            collect, use, disclose, transfer, and store your information.
          </p>
          <p className="mt-4 text-xs text-gray-500">We use Google reCAPTCHA to protect our site.</p>
        </Page>
      </Container>
    </div>
  );
}
